import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

const LegalPage = ({ title, children }) => (
  <div className="min-h-screen bg-slate-50">
    <div className="max-w-3xl mx-auto px-5 md:px-8 py-12">
      <Link 
        to="/" 
        className="inline-flex items-center gap-2 text-slate-500 hover:text-indigo-600 transition-colors mb-8"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Home
      </Link>
      <h1 className="text-3xl md:text-4xl font-bold text-slate-800 mb-2">{title}</h1>
      <p className="text-slate-500 mb-8">Last updated: February 27, 2026</p>
      <div className="prose prose-slate max-w-none">
        {children}
      </div>
    </div>
  </div>
);

export const TermsPage = () => (
  <LegalPage title="Terms of Service">
    <div className="space-y-6 text-slate-600">
      <section>
        <h2 className="text-xl font-semibold text-slate-800 mb-3">1. Acceptance of Terms</h2>
        <p>By accessing or using BrandContentFlow ("the Service"), you agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use our Service.</p>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-slate-800 mb-3">2. Description of Service</h2>
        <p>BrandContentFlow is an AI-powered content transformation platform that helps users repurpose content across multiple social media platforms and marketing channels. The Service includes:</p>
        <ul className="list-disc pl-6 mt-2 space-y-1">
          <li>AI-powered content transformation and adaptation</li>
          <li>Multi-platform content distribution tools</li>
          <li>Brand voice customization features</li>
          <li>Content scheduling and management capabilities</li>
        </ul>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-slate-800 mb-3">3. User Accounts</h2>
        <p>To access certain features of the Service, you must create an account. You are responsible for:</p>
        <ul className="list-disc pl-6 mt-2 space-y-1">
          <li>Maintaining the confidentiality of your account credentials</li>
          <li>All activities that occur under your account</li>
          <li>Notifying us immediately of any unauthorized use</li>
        </ul>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-slate-800 mb-3">4. Acceptable Use</h2>
        <p>You agree not to use the Service to:</p>
        <ul className="list-disc pl-6 mt-2 space-y-1">
          <li>Violate any applicable laws or regulations</li>
          <li>Infringe upon intellectual property rights of others</li>
          <li>Distribute harmful, offensive, or misleading content</li>
          <li>Attempt to gain unauthorized access to the Service</li>
          <li>Interfere with the proper functioning of the Service</li>
        </ul>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-slate-800 mb-3">5. Intellectual Property</h2>
        <p>The Service and its original content, features, and functionality are owned by BrandContentFlow and are protected by international copyright, trademark, and other intellectual property laws. You retain ownership of content you create using our Service.</p>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-slate-800 mb-3">6. Subscription and Payments</h2>
        <p>Some features of the Service require a paid subscription. By subscribing, you agree to pay all applicable fees. Subscription fees are billed in advance on a monthly or annual basis and are non-refundable except as required by law.</p>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-slate-800 mb-3">7. Limitation of Liability</h2>
        <p>To the maximum extent permitted by law, BrandContentFlow shall not be liable for any indirect, incidental, special, consequential, or punitive damages resulting from your use of the Service.</p>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-slate-800 mb-3">8. Changes to Terms</h2>
        <p>We reserve the right to modify these Terms at any time. We will notify users of significant changes via email or through the Service. Your continued use of the Service after changes constitutes acceptance of the new Terms.</p>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-slate-800 mb-3">9. Contact Information</h2>
        <p>For questions about these Terms, please contact us at:</p>
        <p className="mt-2">
          <strong>Email:</strong> info@brandcontentflow.com<br />
          <strong>Address:</strong> 1 Canada Square, Canary Wharf, London E14 5AB, UK
        </p>
      </section>
    </div>
  </LegalPage>
);

export const PrivacyPage = () => (
  <LegalPage title="Privacy Policy">
    <div className="space-y-6 text-slate-600">
      <section>
        <h2 className="text-xl font-semibold text-slate-800 mb-3">1. Introduction</h2>
        <p>BrandContentFlow ("we", "our", or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our Service.</p>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-slate-800 mb-3">2. Information We Collect</h2>
        <h3 className="font-medium text-slate-700 mt-4 mb-2">Personal Information</h3>
        <ul className="list-disc pl-6 space-y-1">
          <li>Name and email address</li>
          <li>Company name and job title</li>
          <li>Billing information and payment details</li>
          <li>Communication preferences</li>
        </ul>
        
        <h3 className="font-medium text-slate-700 mt-4 mb-2">Usage Information</h3>
        <ul className="list-disc pl-6 space-y-1">
          <li>Log data (IP address, browser type, pages visited)</li>
          <li>Device information</li>
          <li>Content you create or upload to the Service</li>
          <li>Feature usage patterns</li>
        </ul>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-slate-800 mb-3">3. How We Use Your Information</h2>
        <p>We use the information we collect to:</p>
        <ul className="list-disc pl-6 mt-2 space-y-1">
          <li>Provide, maintain, and improve our Service</li>
          <li>Process transactions and send related information</li>
          <li>Send promotional communications (with your consent)</li>
          <li>Respond to your comments, questions, and requests</li>
          <li>Monitor and analyze trends, usage, and activities</li>
          <li>Detect, investigate, and prevent fraudulent activities</li>
        </ul>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-slate-800 mb-3">4. Data Sharing</h2>
        <p>We may share your information with:</p>
        <ul className="list-disc pl-6 mt-2 space-y-1">
          <li>Service providers who assist in operating our Service</li>
          <li>Business partners with your consent</li>
          <li>Law enforcement when required by law</li>
          <li>Other parties in connection with a merger or acquisition</li>
        </ul>
        <p className="mt-2">We do not sell your personal information to third parties.</p>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-slate-800 mb-3">5. Data Security</h2>
        <p>We implement appropriate technical and organizational measures to protect your personal information, including:</p>
        <ul className="list-disc pl-6 mt-2 space-y-1">
          <li>Encryption of data in transit and at rest</li>
          <li>Regular security assessments</li>
          <li>Access controls and authentication</li>
          <li>Employee training on data protection</li>
        </ul>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-slate-800 mb-3">6. Your Rights</h2>
        <p>Depending on your location, you may have the right to:</p>
        <ul className="list-disc pl-6 mt-2 space-y-1">
          <li>Access the personal information we hold about you</li>
          <li>Request correction of inaccurate data</li>
          <li>Request deletion of your data</li>
          <li>Object to processing of your data</li>
          <li>Request data portability</li>
          <li>Withdraw consent at any time</li>
        </ul>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-slate-800 mb-3">7. Data Retention</h2>
        <p>We retain your personal information for as long as necessary to fulfill the purposes outlined in this Privacy Policy, unless a longer retention period is required by law.</p>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-slate-800 mb-3">8. International Transfers</h2>
        <p>Your information may be transferred to and processed in countries other than your own. We ensure appropriate safeguards are in place for such transfers in compliance with applicable data protection laws.</p>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-slate-800 mb-3">9. Contact Us</h2>
        <p>For privacy-related inquiries, please contact our Data Protection Officer:</p>
        <p className="mt-2">
          <strong>Email:</strong> info@brandcontentflow.com<br />
          <strong>Address:</strong> 1 Canada Square, Canary Wharf, London E14 5AB, UK
        </p>
      </section>
    </div>
  </LegalPage>
);

export const CookiesPage = () => (
  <LegalPage title="Cookie Policy">
    <div className="space-y-6 text-slate-600">
      <section>
        <h2 className="text-xl font-semibold text-slate-800 mb-3">1. What Are Cookies</h2>
        <p>Cookies are small text files stored on your device when you visit a website. They help the website remember your preferences and understand how you use the site. We use cookies and similar technologies to enhance your experience on BrandContentFlow.</p>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-slate-800 mb-3">2. Types of Cookies We Use</h2>
        
        <h3 className="font-medium text-slate-700 mt-4 mb-2">Essential Cookies</h3>
        <p>These cookies are necessary for the Service to function properly. They enable core functionality such as security, account access, and remembering your preferences. You cannot opt out of these cookies.</p>
        
        <h3 className="font-medium text-slate-700 mt-4 mb-2">Analytics Cookies</h3>
        <p>We use analytics cookies to understand how visitors interact with our Service. This helps us improve our features and user experience. These cookies collect information anonymously.</p>
        
        <h3 className="font-medium text-slate-700 mt-4 mb-2">Functionality Cookies</h3>
        <p>These cookies remember choices you make (such as language preferences) to provide a more personalized experience.</p>
        
        <h3 className="font-medium text-slate-700 mt-4 mb-2">Marketing Cookies</h3>
        <p>With your consent, we may use marketing cookies to deliver relevant advertisements and track the effectiveness of our marketing campaigns.</p>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-slate-800 mb-3">3. Third-Party Cookies</h2>
        <p>Some cookies are placed by third-party services that appear on our pages. We use the following third-party services:</p>
        <ul className="list-disc pl-6 mt-2 space-y-1">
          <li><strong>Google Analytics:</strong> For website traffic analysis</li>
          <li><strong>Stripe:</strong> For secure payment processing</li>
          <li><strong>Intercom:</strong> For customer support chat</li>
        </ul>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-slate-800 mb-3">4. Managing Cookies</h2>
        <p>You can control and manage cookies in several ways:</p>
        <ul className="list-disc pl-6 mt-2 space-y-1">
          <li><strong>Browser settings:</strong> Most browsers allow you to refuse or delete cookies through their settings menu.</li>
          <li><strong>Our cookie banner:</strong> When you first visit our site, you can choose which types of cookies to accept.</li>
          <li><strong>Opt-out links:</strong> Many third-party advertisers offer opt-out mechanisms.</li>
        </ul>
        <p className="mt-2">Please note that disabling certain cookies may affect the functionality of our Service.</p>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-slate-800 mb-3">5. Cookie Duration</h2>
        <p>Cookies can be either "session" or "persistent":</p>
        <ul className="list-disc pl-6 mt-2 space-y-1">
          <li><strong>Session cookies:</strong> Deleted when you close your browser</li>
          <li><strong>Persistent cookies:</strong> Remain on your device for a set period or until you delete them</li>
        </ul>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-slate-800 mb-3">6. Updates to This Policy</h2>
        <p>We may update this Cookie Policy from time to time to reflect changes in our practices or for operational, legal, or regulatory reasons. Please revisit this page periodically to stay informed.</p>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-slate-800 mb-3">7. Contact Us</h2>
        <p>If you have questions about our use of cookies, please contact us:</p>
        <p className="mt-2">
          <strong>Email:</strong> info@brandcontentflow.com<br />
          <strong>Address:</strong> 1 Canada Square, Canary Wharf, London E14 5AB, UK
        </p>
      </section>
    </div>
  </LegalPage>
);

export const NotFoundPage = () => (
  <div className="min-h-screen bg-gradient-to-br from-slate-800 via-purple-800/90 to-slate-800 flex items-center justify-center px-5">
    <div className="text-center">
      <h1 className="text-8xl md:text-9xl font-black text-white/10 mb-4">404</h1>
      <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">Page Not Found</h2>
      <p className="text-white/70 mb-8 max-w-md mx-auto">
        The page you're looking for doesn't exist or has been moved. Our social media profiles are coming soon!
      </p>
      <Link 
        to="/" 
        className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-violet-500 to-pink-500 text-white font-semibold rounded-xl hover:opacity-90 transition-opacity"
      >
        <ArrowLeft className="w-5 h-5" />
        Back to Home
      </Link>
    </div>
  </div>
);
